import { getCountry, getCurrencyMeta, type CurrencyCode } from '@/data/locales';

let _currency: CurrencyCode | string = 'PKR';
let _intlLocale = 'en-PK';

/** Call when company profile/settings load so formatters use company currency. */
export function setLocaleContext(opts: {
  currency?: string | null;
  country?: string | null;
  uiLanguage?: string | null;
}) {
  const country = getCountry(opts.country);
  _currency = opts.currency || country.currency;
  const uiLang = opts.uiLanguage || 'en';
  if (uiLang === 'ar' || uiLang === country.localLanguage) {
    _intlLocale = country.intlLocale;
  } else {
    const region = country.intlLocale.split('-')[1] || country.code;
    _intlLocale = `en-${region}`;
  }
}

export function getActiveCurrency(): string {
  return _currency || 'PKR';
}

export function getActiveIntlLocale(): string {
  return _intlLocale || 'en-PK';
}

export function getCurrencySymbol(currency?: string | null): string {
  return getCurrencyMeta(currency || _currency).symbol;
}

const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'OMR', 'KWD']);

export function getMoneyDecimals(currency?: string | null): number {
  const c = String(currency || _currency || 'PKR').toUpperCase();
  return THREE_DECIMAL_CURRENCIES.has(c) ? 3 : 2;
}

/** Round monetary values to currency-appropriate decimal places (2 for most, 3 for BHD/OMR/KWD). */
export function roundMoney(amount: number | null | undefined, currency?: string | null): number {
  const n = Number(amount) || 0;
  const decimals = getMoneyDecimals(currency);
  const factor = 10 ** decimals;
  return Math.round((n + Number.EPSILON) * factor) / factor;
}

/** Line total: qty × rate, rounded for display and totals. */
export function calcLineAmount(qty: number, rate: number, currency?: string | null): number {
  return roundMoney(qty * rate, currency);
}

/** Official Saudi Riyal sign (Unicode U+20C1), approved by SAMA Feb 2025 */
export const SAR_SYMBOL = '\u20C1';

function formatAmount(n: number, locale: string, decimals: number): string {
  return n.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

export function formatMoney(
  amount: number | null | undefined,
  opts?: { currency?: string | null; locale?: string | null; showSymbol?: boolean },
): string {
  const currency = String(opts?.currency || _currency || 'PKR').toUpperCase();
  const n = roundMoney(Number(amount) || 0, currency);
  const meta = getCurrencyMeta(currency);
  const symbol = currency === 'SAR' ? SAR_SYMBOL : meta.symbol;
  const locale = opts?.locale || _intlLocale || 'en-PK';
  const decimals = THREE_DECIMAL_CURRENCIES.has(currency) ? 3 : 2;
  const formatted = formatAmount(n, locale, decimals);
  if (opts?.showSymbol === false) return formatted;
  // GCC Arabic locales: official riyal sign typically precedes the amount (SAMA guidance)
  if (currency === 'SAR' && locale.startsWith('ar')) {
    return `${symbol}\u00A0${formatted}`;
  }
  return `${symbol} ${formatted}`;
}

export function formatDate(value: string | Date | null | undefined, opts?: { locale?: string | null }): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return String(value);
  const locale = opts?.locale || _intlLocale || 'en-PK';
  return d.toLocaleDateString(locale);
}
